import {MouseEvent, ReactNode} from "react";
import {css, cx} from "@panda/css";

import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat} from "@pg-mono/string-utils";

import {PropertyViewRedirectButton} from "../atoms/PropertyBox/PropertyViewRedirectButton";
import {useResponsiveLinkTarget} from "../hooks/use_responsive_link_target";
import {IMapOfferSidebar} from "../offer/map/types/MapOfferSidebar";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {OfferType} from "../offer/types/OfferType";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {gtmEventPropertyListClick} from "../tracking/google_tag_manager/gtm_event_property_list";
import {gtmEventPropertyListPrice} from "../tracking/google_tag_manager/gtm_event_property_list_price";
import {PropertyListPlanButton} from "./PropertyListPlanButton";
import {propertiesSortOptions} from "./PropertyListTable";

interface IProps {
    property: IPropertyListProperty;
    offer: IOfferDetail | IMapOfferSidebar;
    sort: string | null;
    onPlanClick: (e: MouseEvent<HTMLElement>, index: number) => void;
    index: number;
    showPriceColumn: boolean;
    withoutLink?: boolean;
    hideCell?: {
        propertyPlan?: boolean;
        propertyNumber?: boolean;
        propertyRooms?: boolean;
        propertyArea?: boolean;
        plotArea?: boolean;
        propertyFloor?: boolean;
        propertyPrice?: boolean;
        propertyGotoLink?: boolean;
    };
    handleCellClick?: (value: number) => void;
    textLinkButton?: boolean;
    icon?: ReactNode;
}

export const floorOrFloors = (offerType: OfferType, propertyFloors: number, propertyFloor: number) => {
    if (offerType === OfferType.HOUSE && propertyFloors >= 1) {
        return numberFormat(propertyFloors);
    } else if (propertyFloor != null) {
        if (propertyFloor === 0) {
            return "Parter";
        } else {
            return numberFormat(propertyFloor);
        }
    }
    return "-";
};

export const PropertyListTableRow = (props: IProps) => {
    const {property, offer, sort, onPlanClick, index, showPriceColumn, withoutLink, hideCell, handleCellClick, textLinkButton, icon} = props;

    const responsiveLinkTarget = useResponsiveLinkTarget();

    const isActive = (columnSort: string) => sort && (columnSort === sort || `-${columnSort}` === sort);

    const activeNumberClass = isActive(propertiesSortOptions.NUMBER.value) ? "activeCell" : "";
    const activeRoomClass = isActive(propertiesSortOptions.ROOMS.value) ? "activeCell" : "";
    const activePlotAreaClass = isActive(propertiesSortOptions.PLOT_AREA.value) ? "activeCell" : "";
    const activeAreaClass = isActive(propertiesSortOptions.AREA.value) ? "activeCell" : "";
    const activeFloorClass = isActive(propertiesSortOptions.FLOOR.value) ? "activeCell" : "";
    const activePriceClass = isActive(propertiesSortOptions.PRICE.value) ? "activeCell" : "";

    const handleCellClickHandler = () => {
        if (handleCellClick) {
            handleCellClick(index);
        }
        gtmEventPropertyListPrice(index);
        gtmEventPropertyListClick("nieruchomosc");
    };

    const handleLinkClick = () => {
        gtmEventPropertyListPrice(index);
        gtmEventPropertyListClick("nieruchomosc");
    };

    const link = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const handleRowClick = () => {
        if (withoutLink) {
            handleCellClickHandler();
            return;
        }

        handleLinkClick();

        if (responsiveLinkTarget === "_blank") {
            window.open(link, "_blank");
        } else {
            window.location.href = link;
        }
    };

    const [propertyPlan] = property.plan_image_pages;

    return (
        <tr className={tableRow} onClick={handleRowClick}>
            <PropertyListTableCell data-testid="property-plan" planWrapper showOnMobile hideCell={hideCell?.propertyPlan}>
                {property.plan && propertyPlan && (
                    <PropertyListPlanButton
                        index={index}
                        onPlanClick={(e, idx) => {
                            gtmEventPropertyListClick("rzut");
                            onPlanClick(e, idx);
                        }}
                    />
                )}
            </PropertyListTableCell>

            <PropertyListTableCell className={activeNumberClass} data-testid="property-number" hideCell={hideCell?.propertyNumber}>
                <Text variant="body_copy_2" align="center">
                    {property.number}
                </Text>
            </PropertyListTableCell>

            {(offer.type === OfferType.HOUSE || offer.type === OfferType.FLAT) && (
                <PropertyListTableCell className={activeRoomClass} data-testid="property-rooms" showOnMobile hideCell={hideCell?.propertyRooms}>
                    <Text variant="body_copy_2" align="center">
                        {property.rooms}
                    </Text>
                </PropertyListTableCell>
            )}

            <PropertyListTableCell className={activeAreaClass} data-testid="property-area" showOnMobile hideCell={hideCell?.propertyArea}>
                <Text variant="body_copy_2" align="center">
                    {areaFormat(property.area, {precision: 2})}
                </Text>
            </PropertyListTableCell>

            {offer.type === OfferType.HOUSE ? (
                <PropertyListTableCell className={activePlotAreaClass} data-testid="plot-area" hideCell={hideCell?.plotArea}>
                    <Text variant="body_copy_2" align="center">
                        {property.plot_area ? (
                            <>
                                {numberFormat(property.plot_area, {precision: 2})} m<sup>2</sup>
                            </>
                        ) : (
                            "-"
                        )}
                    </Text>
                </PropertyListTableCell>
            ) : (
                <PropertyListTableCell className={activeFloorClass} data-testid="property-floor" hideCell={hideCell?.propertyFloor}>
                    <Text variant="body_copy_2" align="center">
                        {floorOrFloors(offer.type, property.floors, property.floor)}
                    </Text>
                </PropertyListTableCell>
            )}

            {showPriceColumn && (
                <PropertyListTableCell
                    className={cx(activePriceClass, priceCell)}
                    textLinkButton={textLinkButton}
                    data-testid="property-price"
                    showOnMobile
                    hideCell={hideCell?.propertyPrice}
                >
                    <PropertyViewRedirectButton buttonVariant="highlight_primary" property={property} link={!withoutLink ? link : undefined} />
                </PropertyListTableCell>
            )}

            <PropertyListTableCell data-testid="property-goto-link" showOnMobile hideCell={hideCell?.propertyGotoLink}>
                <div className={arrowWrapper}>{icon ? icon : <ChevronRightIcon size="3" fill="var(--colors-gray-700)" />}</div>
            </PropertyListTableCell>
        </tr>
    );
};

interface PropertyListTableCellProps {
    children: React.ReactNode;

    className?: string;
    showOnMobile?: boolean;
    hideCell?: boolean;
    textLinkButton?: boolean;
    planWrapper?: boolean;

    "data-testid"?: string;
}

export const PropertyListTableCell = ({children, ...rowCellProps}: PropertyListTableCellProps) => {
    return <RowCell {...rowCellProps}>{children}</RowCell>;
};

const tableRow = css({
    transition: "background 0.2s ease-in-out",
    cursor: "pointer",
    color: "var(--colors-secondary)",

    "&:nth-of-type(odd)": {
        background: "var(--colors-gray-100)",
        sm: {
            backgroundColor: "transparent"
        },
        "&:hover": {
            background: "var(--colors-gray-400)"
        }
    },

    "&:nth-of-type(even)": {
        background: "var(--colors-gray-200)",
        "&:hover": {
            background: "var(--colors-gray-400)"
        }
    },

    "& .activeCell": {
        background: "var(--colors-background-200)"
    }
});

const arrowWrapper = css({
    transform: "scale(50%, 50%)",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyItems: "center",
    alignItems: "center"
});

interface IRowProps {
    showOnMobile?: boolean;
    planWrapper?: boolean;
    hideCell?: boolean;
    textLinkButton?: boolean;
}

const RowCell = (props: IRowProps & {children: ReactNode; className?: string; "data-testid"?: string}) => {
    const {showOnMobile, planWrapper, hideCell, textLinkButton, children, className: additionalClassName, "data-testid": dataTestId} = props;

    const cellClass = cx(
        css({
            verticalAlign: "middle",
            minHeight: "54px",
            height: "54px",
            padding: "1rem",
            border: 0,

            xs: {
                minHeight: "64px"
            },

            display: showOnMobile && !hideCell ? "table-cell" : "none",
            sm: {
                display: hideCell ? "none" : "table-cell"
            }
        }),
        planWrapper &&
            css({
                paddingLeft: "2rem",
                xs: {
                    padding: "0 0 0 3rem"
                }
            }),
        textLinkButton &&
            css({
                "& button": {
                    fontStyle: "normal",
                    fontWeight: "normal",
                    textTransform: "lowercase",
                    backgroundColor: "transparent",
                    border: "none",
                    paddingLeft: 0,

                    "&:hover, &:active, &:focus, &:focus:active": {
                        backgroundColor: "transparent",
                        textDecoration: "underline"
                    },

                    "& span:first-letter": {
                        textTransform: "uppercase"
                    }
                }
            }),
        additionalClassName
    );

    return (
        <td className={cellClass} data-testid={dataTestId}>
            {children}
        </td>
    );
};

const priceCell = css({
    textAlign: "center"
});
import {MouseEvent, ReactNode} from "react";
import {css, cx} from "@panda/css";

import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat} from "@pg-mono/string-utils";

import {PropertyViewRedirectButton} from "../atoms/PropertyBox/PropertyViewRedirectButton";
import {useResponsiveLinkTarget} from "../hooks/use_responsive_link_target";
import {IMapOfferSidebar} from "../offer/map/types/MapOfferSidebar";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {OfferType} from "../offer/types/OfferType";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {gtmEventPropertyListClick} from "../tracking/google_tag_manager/gtm_event_property_list";
import {gtmEventPropertyListPrice} from "../tracking/google_tag_manager/gtm_event_property_list_price";
import {PropertyListPlanButton} from "./PropertyListPlanButton";
import {propertiesSortOptions} from "./PropertyListTable";

interface IProps {
    property: IPropertyListProperty;
    offer: IOfferDetail | IMapOfferSidebar;
    sort: string | null;
    onPlanClick: (e: MouseEvent<HTMLElement>, index: number) => void;
    index: number;
    showPriceColumn: boolean;
    withoutLink?: boolean;
    hideCell?: {
        propertyPlan?: boolean;
        propertyNumber?: boolean;
        propertyRooms?: boolean;
        propertyArea?: boolean;
        plotArea?: boolean;
        propertyFloor?: boolean;
        propertyPrice?: boolean;
        propertyGotoLink?: boolean;
    };
    handleCellClick?: (value: number) => void;
    textLinkButton?: boolean;
    icon?: ReactNode;
}

export const floorOrFloors = (offerType: OfferType, propertyFloors: number, propertyFloor: number) => {
    if (offerType === OfferType.HOUSE && propertyFloors >= 1) {
        return numberFormat(propertyFloors);
    } else if (propertyFloor != null) {
        if (propertyFloor === 0) {
            return "Parter";
        } else {
            return numberFormat(propertyFloor);
        }
    }
    return "-";
};

export const PropertyListTableRow = (props: IProps) => {
    const {property, offer, sort, onPlanClick, index, showPriceColumn, withoutLink, hideCell, handleCellClick, textLinkButton, icon} = props;

    const responsiveLinkTarget = useResponsiveLinkTarget();

    const isActive = (columnSort: string) => sort && (columnSort === sort || `-${columnSort}` === sort);

    const activeNumberClass = isActive(propertiesSortOptions.NUMBER.value) ? "activeCell" : "";
    const activeRoomClass = isActive(propertiesSortOptions.ROOMS.value) ? "activeCell" : "";
    const activePlotAreaClass = isActive(propertiesSortOptions.PLOT_AREA.value) ? "activeCell" : "";
    const activeAreaClass = isActive(propertiesSortOptions.AREA.value) ? "activeCell" : "";
    const activeFloorClass = isActive(propertiesSortOptions.FLOOR.value) ? "activeCell" : "";
    const activePriceClass = isActive(propertiesSortOptions.PRICE.value) ? "activeCell" : "";

    const handleCellClickHandler = () => {
        if (handleCellClick) {
            handleCellClick(index);
        }
        gtmEventPropertyListPrice(index);
        gtmEventPropertyListClick("nieruchomosc");
    };

    const handleLinkClick = () => {
        gtmEventPropertyListPrice(index);
        gtmEventPropertyListClick("nieruchomosc");
    };

    const link = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const handleRowClick = () => {
        if (withoutLink) {
            handleCellClickHandler();
            return;
        }

        handleLinkClick();

        if (responsiveLinkTarget === "_blank") {
            window.open(link, "_blank");
        } else {
            window.location.href = link;
        }
    };

    const [propertyPlan] = property.plan_image_pages;

    return (
        <tr className={tableRow} onClick={handleRowClick}>
            <PropertyListTableCell data-testid="property-plan" planWrapper showOnMobile hideCell={hideCell?.propertyPlan}>
                {property.plan && propertyPlan && (
                    <PropertyListPlanButton
                        index={index}
                        onPlanClick={(e, idx) => {
                            gtmEventPropertyListClick("rzut");
                            onPlanClick(e, idx);
                        }}
                    />
                )}
            </PropertyListTableCell>

            <PropertyListTableCell className={activeNumberClass} data-testid="property-number" hideCell={hideCell?.propertyNumber}>
                <Text variant="body_copy_2" align="center">
                    {property.number}
                </Text>
            </PropertyListTableCell>

            {(offer.type === OfferType.HOUSE || offer.type === OfferType.FLAT) && (
                <PropertyListTableCell className={activeRoomClass} data-testid="property-rooms" showOnMobile hideCell={hideCell?.propertyRooms}>
                    <Text variant="body_copy_2" align="center">
                        {property.rooms}
                    </Text>
                </PropertyListTableCell>
            )}

            <PropertyListTableCell className={activeAreaClass} data-testid="property-area" showOnMobile hideCell={hideCell?.propertyArea}>
                <Text variant="body_copy_2" align="center">
                    {areaFormat(property.area, {precision: 2})}
                </Text>
            </PropertyListTableCell>

            {offer.type === OfferType.HOUSE ? (
                <PropertyListTableCell className={activePlotAreaClass} data-testid="plot-area" hideCell={hideCell?.plotArea}>
                    <Text variant="body_copy_2" align="center">
                        {property.plot_area ? (
                            <>
                                {numberFormat(property.plot_area, {precision: 2})} m<sup>2</sup>
                            </>
                        ) : (
                            "-"
                        )}
                    </Text>
                </PropertyListTableCell>
            ) : (
                <PropertyListTableCell className={activeFloorClass} data-testid="property-floor" hideCell={hideCell?.propertyFloor}>
                    <Text variant="body_copy_2" align="center">
                        {floorOrFloors(offer.type, property.floors, property.floor)}
                    </Text>
                </PropertyListTableCell>
            )}

            {showPriceColumn && (
                <PropertyListTableCell
                    className={cx(activePriceClass, priceCell)}
                    textLinkButton={textLinkButton}
                    data-testid="property-price"
                    showOnMobile
                    hideCell={hideCell?.propertyPrice}
                >
                    <PropertyViewRedirectButton buttonVariant="highlight_primary" property={property} link={!withoutLink ? link : undefined} />
                </PropertyListTableCell>
            )}

            <PropertyListTableCell data-testid="property-goto-link" showOnMobile hideCell={hideCell?.propertyGotoLink}>
                <div className={arrowWrapper}>{icon ? icon : <ChevronRightIcon size="3" fill="var(--colors-gray-700)" />}</div>
            </PropertyListTableCell>
        </tr>
    );
};

interface PropertyListTableCellProps {
    children: React.ReactNode;

    className?: string;
    showOnMobile?: boolean;
    hideCell?: boolean;
    textLinkButton?: boolean;
    planWrapper?: boolean;

    "data-testid"?: string;
}

export const PropertyListTableCell = ({children, ...rowCellProps}: PropertyListTableCellProps) => {
    return <RowCell {...rowCellProps}>{children}</RowCell>;
};

const tableRow = css({
    transition: "background 0.2s ease-in-out",
    cursor: "pointer",
    color: "var(--colors-secondary)",

    "&:nth-of-type(odd)": {
        background: "var(--colors-gray-100)",
        sm: {
            backgroundColor: "transparent"
        },
        "&:hover": {
            background: "var(--colors-gray-400)"
        }
    },

    "&:nth-of-type(even)": {
        background: "var(--colors-gray-200)",
        "&:hover": {
            background: "var(--colors-gray-400)"
        }
    },

    "& .activeCell": {
        background: "var(--colors-background-200)"
    }
});

const arrowWrapper = css({
    transform: "scale(50%, 50%)",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyItems: "center",
    alignItems: "center"
});

interface IRowProps {
    showOnMobile?: boolean;
    planWrapper?: boolean;
    hideCell?: boolean;
    textLinkButton?: boolean;
}

const RowCell = (props: IRowProps & {children: ReactNode; className?: string; "data-testid"?: string}) => {
    const {showOnMobile, planWrapper, hideCell, textLinkButton, children, className: additionalClassName, "data-testid": dataTestId} = props;

    const cellClass = cx(
        css({
            verticalAlign: "middle",
            minHeight: "54px",
            height: "54px",
            padding: "1rem",
            border: 0,

            xs: {
                minHeight: "64px"
            },

            display: showOnMobile && !hideCell ? "table-cell" : "none",
            sm: {
                display: hideCell ? "none" : "table-cell"
            }
        }),
        planWrapper &&
            css({
                paddingLeft: "2rem",
                xs: {
                    padding: "0 0 0 3rem"
                }
            }),
        textLinkButton &&
            css({
                "& button": {
                    fontStyle: "normal",
                    fontWeight: "normal",
                    textTransform: "lowercase",
                    backgroundColor: "transparent",
                    border: "none",
                    paddingLeft: 0,

                    "&:hover, &:active, &:focus, &:focus:active": {
                        backgroundColor: "transparent",
                        textDecoration: "underline"
                    },

                    "& span:first-letter": {
                        textTransform: "uppercase"
                    }
                }
            }),
        additionalClassName
    );

    return (
        <td className={cellClass} data-testid={dataTestId}>
            {children}
        </td>
    );
};

const priceCell = css({
    textAlign: "center"
});
